import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import withWidth from '@material-ui/core/withWidth';

import { getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import Filter from 'components/common/Filter';
import TabNavigation from 'components/common/TabNavigation';
import SalesRepresentativesTable from 'components/settings/SalesRepresentativesTable';
import SalesRepresentativeCreateEditDialog from 'components/settings/SalesRepresentativeCreateEditDialog';

import {
  fetchSalesRepresentatives,
  clearSalesRepresentatives,
} from 'redux/modules/salesRepresentatives';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';

import { appTitle, titleSeparator } from 'config/helmetSettings';

class MedicalSourcesSales extends Component {
  constructor(props) {
    super(props);

    this.state = {
      createDialogOpen: false,
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
      limit: 10,
      offset: 0,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onFilterButtonClick = this.onFilterButtonClick.bind(this);
    this.onCreateDialogClose = this.onCreateDialogClose.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);
    this.onTableScroll = this.onTableScroll.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    this.props.actions.clearSalesRepresentatives();
  }

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filters, width } = this.props;
    const { limit, offset } = this.state;
    const filtersChanged = nextProps.filters['Full Name'] !== filters['Full Name'];
    let newOffset = offset;
    if (filtersChanged) {
      newOffset = 0;
    }
    if (newOffset !== offset) {
      this.setState({ offset: newOffset });
    }

    if (nextProps.width !== width) {
      this.setState({
        mobileFilterExpanded: ['xs', 'sm'].indexOf(nextProps.width) === -1,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters, salesRepresentatives, count, loading } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['Full Name'],
      },
    );

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (limitChanged || offsetChanged || filtersChanged) {
      this.onRequest();
    } else if (salesRepresentatives.length < count
      && this.scrollRef
      && this.scrollRef.scrollTop === 0
      && loading === 0) {
      this.onTableScroll({ target: this.scrollRef });
    }
  }

  onRequest() {
    const { actions, filters } = this.props;
    const { limit, offset } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map(key => ({
        name: key,
        operation: 'startswith',
        val: filters[key],
        case_sensitivity: false,
      }))
      : null;
    actions.fetchSalesRepresentatives({
      limit,
      offset,
      filters: requestFilters,
      triggerLoading: true,
      push: offset > 0,
    });
  }

  onFilterButtonClick() {
    this.setState({ createDialogOpen: true });
  }

  onCreateDialogClose() {
    this.setState({ createDialogOpen: false });
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  onTableScroll(event) {
    const { offset, limit } = this.state;

    if ((event.target.offsetHeight + event.target.scrollTop) / event.target.scrollHeight > 0.9) {
      if ((this.props.salesRepresentatives.length - limit) >= offset) {
        this.setState({
          offset: offset + limit,
        });
      }
    }
  }

  render() {
    const { salesRepresentatives, count, loading, filters } = this.props;
    const { createDialogOpen, mobileFilterExpanded } = this.state;

    return (
      <Layout variant="settings">
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Sales
          </title>
        </Helmet>
        <TabNavigation
          items={[
            {
              text: 'Referrals',
              href: '/settings/medical-sources/referrals',
            },
            {
              text: 'Sales',
              href: '/settings/medical-sources/sales',
            },

            {
              text: 'Prescribers',
              href: '/settings/medical-sources/prescribers',
            },
            {
              text: 'HCPCS Codes',
              href: '/settings/medical-sources/hcpc',
            },
            {
              text: 'Diagnosis',
              href: '/settings/medical-sources/diagnosis',
            },
            {
              text: 'Payers',
              href: '/settings/medical-sources/payers',
            },
          ]}
        />
        <Filter
          form="salesRepresentativesFilter"
          placeholder="Search Sales Representative"
          primaryButtonText="Add New"
          onPrimaryButtonClick={this.onFilterButtonClick}
          onMobileFilterBottonClick={this.onMobileFilterBottonClick}
          mobileFilterExpanded={mobileFilterExpanded}
          filters={filters}
          showTableCount
          count={count}
          loading={loading}
          items={salesRepresentatives}
          beforeTable
        />
        {loading > 0 &&
          <Progress fixed />
        }
        <SalesRepresentativesTable
          items={salesRepresentatives}
          count={count}
          loading={loading}
          mobileFilterExpanded={mobileFilterExpanded}
          onScroll={this.onTableScroll}
          scrollRef={(scrollRef) => { this.scrollRef = scrollRef; }}
        />
        <SalesRepresentativeCreateEditDialog
          form="salesRepresentativeCreate"
          open={createDialogOpen}
          onClose={this.onCreateDialogClose}
          onSubmitSuccess={() => {
            this.onRequest();
          }}
        />
      </Layout>
    );
  }
}

MedicalSourcesSales.propTypes = {
  salesRepresentatives: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    'Full Name': PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    fetchSalesRepresentatives: PropTypes.func.isRequired,
    clearSalesRepresentatives: PropTypes.func.isRequired,
  }).isRequired,
  width: PropTypes.string.isRequired,
};

MedicalSourcesSales.defaultProps = {};

const mapStateToProps = (store) => {
  const salesRepresentativesFilterValues = getFormValues('salesRepresentativesFilter')(store);
  return {
    salesRepresentatives: store.salesRepresentatives.data,
    count: store.salesRepresentatives.count,
    filters: {
      ...salesRepresentativesFilterValues
        && salesRepresentativesFilterValues.Filter
        ? { 'Full Name': salesRepresentativesFilterValues.Filter }
        : {},
    },
    loading: store.salesRepresentatives.loading,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchSalesRepresentatives,
    clearSalesRepresentatives,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withWidth()(MedicalSourcesSales),
);



// WEBPACK FOOTER //
// ./src/pages/settings/MedicalSourcesSales.js